import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import IpoSearchBox from '../search/IpoSearchBox';
import { ArrowRight, Flame } from 'lucide-react';
import { useLiveIpoFeed } from '../../hooks/useLiveIpoFeed';
import { pickTrending } from '../../utils/tickerFeed';

// Phrases to cycle through with typing animation
const PHRASES = [
  'Mainboard & SME IPOs',
  'High-Growth Unicorns',
  'BSE & NSE Issues',
  'Upcoming Tech IPOs',
];

export const HeroContent = () => {
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [currentText, setCurrentText] = useState(PHRASES[0]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPaused, setIsPaused] = useState(true);

  useEffect(() => {
    const currentFullPhrase = PHRASES[phraseIndex];

    if (isPaused) {
      const pauseTimer = setTimeout(() => {
        setIsPaused(false);
        setIsDeleting(true);
      }, 2400);
      return () => clearTimeout(pauseTimer);
    }

    const typingSpeed = isDeleting ? 45 : 85;

    const timer = setTimeout(() => {
      if (!isDeleting) {
        // Typing forward
        if (currentText.length < currentFullPhrase.length) {
          setCurrentText(currentFullPhrase.slice(0, currentText.length + 1));
        } else {
          // Finished typing phrase, pause before deleting
          setIsPaused(true);
        }
      } else {
        // Backspacing
        if (currentText.length > 0) {
          setCurrentText(currentFullPhrase.slice(0, currentText.length - 1));
        } else {
          // Finished deleting, move to next phrase
          setIsDeleting(false);
          setPhraseIndex((prev) => (prev + 1) % PHRASES.length);
        }
      }
    }, typingSpeed);

    return () => clearTimeout(timer);
  }, [currentText, isDeleting, isPaused, phraseIndex]);

  // Live and upcoming issues with the strongest grey market premium (GET /api/market/gmp)
  const { items, counts, loading } = useLiveIpoFeed();
  const trendingItems = pickTrending(items);

  return (
    <div className="flex flex-col justify-center space-y-6 lg:space-y-7">
      
      {/* Real-Time Primary Market Terminal Badge */}
      <div>
        <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-50/90 border border-emerald-200/80 text-emerald-800 text-xs font-semibold shadow-xs">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          <span>Real-Time Primary Market Terminal</span>
        </span>
      </div>

      {/* Main Headline with Stable Typewriter Animation on Dedicated Line */}
      <div className="space-y-2 animate-title-slide">
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[46px] xl:text-[50px] font-extrabold text-slate-900 tracking-tight leading-[1.15]">
          <span className="block">Invest in India’s Next</span>
          <span className="block min-h-[1.25em] mt-0.5 sm:mt-1">
            <span className="relative inline-flex items-center">
              <span className="bg-gradient-to-r from-[#006666] via-[#008080] via-[#009696] to-[#004d4d] bg-clip-text text-transparent animate-gradient-text font-extrabold">
                {currentText}
              </span>
              {/* Blinking Typewriter Cursor */}
              <span className="inline-block w-[3px] sm:w-[4px] h-[0.85em] bg-[#008080] ml-1.5 align-middle rounded-full animate-pulse shadow-sm shadow-[#008080]/50" />
              {/* Ambient accent pulse underline */}
              <span className="absolute -bottom-1 left-0 right-0 h-[2.5px] bg-gradient-to-r from-[#008080]/10 via-[#008080]/60 to-[#2dd4bf]/20 rounded-full blur-[0.5px] animate-pulse" />
            </span>
          </span>
        </h1>
        <p className="text-slate-600 text-sm sm:text-base md:text-lg max-w-xl font-normal leading-relaxed pt-1">
          Track live Grey Market Premiums (GMP), category-wise subscription from the exchange
          bid book, and every issue from DRHP filing to listing.
        </p>
      </div>



      {/* Search Input Card */}
      <div className="relative max-w-xl">
        <div className="relative flex items-center shadow-soft rounded-2xl bg-white border border-slate-200/90 focus-within:border-[#008080] focus-within:ring-4 focus-within:ring-[#008080]/10 transition-all p-1.5">
          <IpoSearchBox
            placeholder="Search IPOs by company name..."
            inputClassName="w-full pl-10 pr-9 py-2.5 text-sm sm:text-base text-slate-900 placeholder:text-slate-400 bg-transparent focus:outline-none"
          >
            <button
              type="submit"
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 flex-shrink-0 inline-flex items-center gap-1.5 bg-[#008080] hover:bg-[#006666] active:bg-[#004d4d] text-white font-semibold text-xs sm:text-sm px-4 sm:px-5 py-2.5 rounded-xl shadow-sm shadow-[#008080]/20 transition-all"
            >
              <span>Search</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </IpoSearchBox>
        </div>
      </div>

      {/* Trending: live issues from the GMP board */}
      {(loading || trendingItems.length > 0) && (
        <div className="flex flex-wrap items-center gap-2 pt-0.5">
          <span className="text-xs font-semibold text-slate-500 mr-1">Trending:</span>
          {loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <span key={i} className="h-7 w-24 rounded-xl bg-slate-100 animate-pulse" aria-hidden="true" />
              ))
            : trendingItems.map((item) => {
                const chip = (
                  <>
                    <Flame className="w-3.5 h-3.5 text-orange-500" aria-hidden="true" />
                    <span>{item.name}</span>
                    {item.gmp && <span className={item.gmpPercent < 0 ? 'text-rose-600' : 'text-emerald-600'}>{item.gmp}</span>}
                  </>
                );
                const className =
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium bg-white border border-slate-200 text-slate-700 hover:border-teal-300 hover:bg-teal-50/50 hover:text-[#006666] transition-all shadow-xs';
                return item.link ? (
                  <Link key={item.id} to={item.link} className={className}>
                    {chip}
                  </Link>
                ) : (
                  <span key={item.id} className={className}>
                    {chip}
                  </span>
                );
              })}
        </div>
      )}

      {/* Live market counts */}
      <div className="pt-2 border-t border-slate-100 flex flex-wrap items-center gap-3 sm:gap-4 text-xs text-slate-600">
        <div className="font-medium text-slate-700">
          <strong className="text-slate-900">{loading ? '…' : counts.open}</strong> IPOs open now
        </div>

        <span className="text-slate-300">•</span>

        <div className="font-medium text-slate-700">
          <strong className="text-slate-900">{loading ? '…' : counts.upcoming}</strong> opening soon
        </div>

        <span className="text-slate-300">•</span>

        {/* 100% Free Live Terminal */}
        <div className="font-medium text-emerald-600">
          100% Free Live Terminal
        </div>
      </div>
    </div>
  );
};

export default HeroContent;
